import React, { useState, useCallback } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CircularProgress from "@mui/material/CircularProgress";
import { Description as ExcelIcon } from "@mui/icons-material";

import { EXPORT_GROUPED_ASSETS_EXCEL_MUTATION } from "../../../constants/graphql/mutations";
import useMutation from "../../../hooks/useMutation";
import { useBanner } from "../../../contexts/banner";

const styles = {
  searchContainer: {
    mb: 0,
    position: "sticky",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    zIndex: 10,
    padding: "16px 0",
  },
};

export default function SearchAndExport({
  projectId,
  onSearch,
  searchLoading,
  displayExport = true,
}) {
  const [inputValue, setInputValue] = useState("");
  const [exportGroupedAssetsExcel] = useMutation(EXPORT_GROUPED_ASSETS_EXCEL_MUTATION);
  const { displayBanner } = useBanner();

  const handleInputChange = useCallback(event => {
    setInputValue(event.target.value);
  }, []);

  const handleKeyPress = useCallback(
    event => {
      if (event.key === "Enter") {
        onSearch(inputValue);
      }
    },
    [onSearch, inputValue]
  );

  const handleSearchClick = useCallback(() => {
    onSearch(inputValue);
  }, [onSearch, inputValue]);

  const handleClearSearch = useCallback(() => {
    setInputValue("");
    onSearch("");
  }, [onSearch]);

  const handleExportExcel = useCallback(() => {
    try {
      displayBanner({
        message: "Generating file...",
        type: "export-excel",
        projectId,
        startedAt: Date.now(),
      });
      exportGroupedAssetsExcel({
        variables: { projectId },
      });
    } catch (e) {
      console.error("Export grouped assets error:", e);
    }
  }, [displayBanner, exportGroupedAssetsExcel, projectId]);

  return (
    <Box sx={styles.searchContainer}>
      <TextField
        variant="outlined"
        placeholder="Search"
        value={inputValue}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        size="small"
        sx={{
          width: displayExport ? "500px" : "100%",
          maxWidth: "100%",
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {inputValue && (
                <IconButton onClick={handleClearSearch}>
                  <CloseIcon />
                </IconButton>
              )}
              <IconButton onClick={handleSearchClick} edge="end" disabled={searchLoading}>
                {searchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {displayExport && (
        <Button onClick={handleExportExcel} variant="outlined" startIcon={<ExcelIcon />}>
          Export to Excel
        </Button>
      )}
    </Box>
  );
}
